import { apiFetch } from '@/lib/api-client';
import type {
  MduRequestQueueItem,
  MduRequestDetail,
  SaveMduFeedbackRequest,
  SaveMduFeedbackResult,
  PutUnitsBackInScopeRequest,
  PutUnitsBackInScopeResult,
  EscalateMduRequestRequest,
  MduEscalationDto,
  MduRequestNoteDto,
} from '@uniticket/shared';

// "MDU not in Appeee" — apiFetch wrappers (no react-query; same style as useAddressValidation).
// Internal-only endpoints (gated server-side to Unifiber staff); the JWT is attached by apiFetch.

export type MduQueueStatus = 'pending' | 'handled' | 'all';

/** The MDU-requests queue (pending by default). Optional free-text search + status filter, PAGED:
 *  `meta.total` is the size of the whole filtered set, so the caller can tell "100 of 1885" from "all 47". */
export async function fetchMduQueue(
  status?: MduQueueStatus, search?: string,
  page?: { limit?: number; offset?: number; refresh?: boolean; mine?: boolean; view?: 'escalatedToMe' },
) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (search && search.trim()) params.set('search', search.trim());
  if (page?.limit != null) params.set('limit', String(page.limit));
  if (page?.offset) params.set('offset', String(page.offset));
  // Le serveur sert la vue « en cours » depuis un instantané ; refresh=1 lui fait relire la base.
  if (page?.refresh) params.set('refresh', '1');
  // Filtres d'escalade : posés côté SERVEUR, jamais en filtrant le tableau reçu — sinon `meta.total`
  // et « Charger plus » annoncent autre chose que ce qui est affiché.
  if (page?.mine) params.set('mine', 'true');
  if (page?.view) params.set('view', page.view);
  const q = params.toString();
  return apiFetch<{
    data: MduRequestQueueItem[];
    meta: {
      total: number; limit: number; offset: number; takenAt?: string; cached?: boolean;
      isEscalationTarget?: boolean;
    };
  }>(`/mdu-requests${q ? `?${q}` : ''}`);
}

/** One request: fields + computed case + matched LUs (per-building breakdown) + feedback. */
export async function fetchMduRequest(id: number, refineOnBusNumber = false) {
  const q = refineOnBusNumber ? '?refine=bus' : '';
  return apiFetch<{ data: MduRequestDetail }>(`/mdu-requests/${id}${q}`);
}

/** Record the reviewer's feedback (upsert). Server refuses (409) unless ADDR_TABLE_TARGET=local; false needs a comment. */
export async function saveMduFeedback(id: number, body: SaveMduFeedbackRequest) {
  return apiFetch<{ data: SaveMduFeedbackResult }>(`/mdu-requests/${id}/feedback`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Cas 2: put the ticked LUs of a building back in the V15 scope. Server refuses (409) unless ADDR_TABLE_TARGET=local. */
export async function putUnitsBackInScope(id: number, body: PutUnitsBackInScopeRequest) {
  return apiFetch<{ data: PutUnitsBackInScopeResult }>(`/mdu-requests/${id}/units-back-in-scope`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Passer la demande à la personne suivante de la chaîne d'escalade. 409 si aucune cible n'est
 *  configurée (l'écran grise déjà le bouton dans ce cas). Rien n'est écrit dans le DMP. */
export async function escalateMduRequest(id: number, body: EscalateMduRequestRequest) {
  return apiFetch<{ data: MduEscalationDto }>(`/mdu-requests/${id}/escalate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Le fil de commentaires INTERNES d'une demande. Ne part nulle part : le canal vers le sous-traitant
 *  reste le feedback. */
export async function listMduRequestNotes(id: number) {
  return apiFetch<{ data: MduRequestNoteDto[] }>(`/mdu-requests/${id}/notes`);
}

/** Ajoute une note et REND LE FIL COMPLET — l'écran n'a donc pas à recharger derrière, et ne peut pas
 *  afficher un fil qui aurait bougé entre-temps. */
export async function addMduRequestNote(id: number, body: string) {
  return apiFetch<{ data: MduRequestNoteDto[] }>(`/mdu-requests/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

/** Rendre la main à celui qui a escaladé. 403 si l'appelant n'est pas le destinataire, 409 si la
 *  demande n'est pas escaladée. */
export async function returnMduEscalation(id: number, body: EscalateMduRequestRequest) {
  return apiFetch<{ data: MduEscalationDto }>(`/mdu-requests/${id}/escalation-return`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
